import React, { useState, useEffect } from 'react';

/**
 * StartPage — the public entry point.
 *
 * Replaces the situation the audit found: `/` opened the raw engine on an empty blue
 * stage with zero POIs, a hamburger full of developer tools and no statement of what
 * the product is (Content post 4437). A visitor now gets a claim, a live proof and two
 * unmistakable next steps.
 *
 * Deliberately dependency-free (inline styles, no Tailwind/shadcn) so it renders
 * identically wherever the router later mounts it.
 *
 * Palette follows Alex' red title-sequence presentation — the product presents itself
 * in its own design language.
 */

const RED = '#C1121C';
const RED_DEEP = '#8E0C14';
const CREAM = '#FBF7F4';
// Kein neutrales Schwarz: Auf der Creme-Fläche wirkt Schwarz wie eine fremde Website
// (Alex-Review 2026-08-03 — "das bricht voll"). Stattdessen bleibt die Seite in EINER
// Farbfamilie; die helle Sektion ist die Umkehrung der roten, nicht ihr Gegenteil.
const INK = '#3D1418';          // sehr dunkles Rotbraun statt Schwarz — für Fließtext
const HEAD_ON_CREAM = '#8E0C14'; // Überschriften auf Creme tragen die Markenfarbe

export interface MarketingHomeProps {
    /** The presentation used as the live hero proof — a local file so the landing
     *  page never depends on Storage/auth being up. */
    heroPresentationId?: string;
    onOpenApp?: () => void;
    onCreate?: () => void;
}

const serif = "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
const sans = "system-ui, -apple-system, 'Segoe UI', sans-serif";

/**
 * Real screenshots of running presentations (headless Chrome against the live site,
 * scripts in .choreo-ref/theme). No renderings, no generated images — Alex' rule for
 * the start page: show the product, don't describe it. Each card links to the live
 * presentation in viewer mode.
 */
const GALLERY: { src: string; title: string; text: string; pres: string }[] = [
    { src: '/marketing/gallery-hex.jpg', title: 'Wabenkugel', text: '102 Arten der Tscheppaschlucht auf einer Kugel aus Sechsecken. Die Kamera bleibt außen, die aktive Wabe trägt Name und lateinischen Namen.', pres: 'flora-hex.md' },
    { src: '/marketing/gallery-hex3d.jpg', title: '3D-Modelle statt Bilder', text: 'Dieselben Tiere als echte 3D-Modelle auf der Kugel. Das aktive Tier trägt sein Label, seine Wissenspunkte sind anklickbar.', pres: 'flora-hex3d.md' },
    { src: '/marketing/gallery-spiral.jpg', title: 'Karten mit Wissenspunkten', text: 'Eine Karte je Art entlang einer Spirale, jede mit nummerierten Wissenspunkten. Die Kamera fliegt von Punkt zu Punkt.', pres: 'flora-spiral.md' },
    { src: '/marketing/gallery-glb.jpg', title: 'Ein Modell, viele Marker', text: 'Ein einzelnes Modell mit nummerierten Wissenspunkten. Der Text zu jedem Punkt kommt aus der Wissensdatenbank.', pres: 'buschwindroeschen-3d.md' },
];

export const MarketingHome: React.FC<MarketingHomeProps> = ({ heroPresentationId = 'hero-story.md', onOpenApp, onCreate }) => {
    const [narrow, setNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 860);

    useEffect(() => {
        const onResize = () => setNarrow(window.innerWidth < 860);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const demoUrl = `${window.location.origin}/?pres=${heroPresentationId}&view=1`;

    const primaryBtn: React.CSSProperties = {
        background: CREAM, color: RED_DEEP, border: 0, borderRadius: 999,
        padding: narrow ? '15px 26px' : '17px 34px', fontSize: narrow ? 16 : 17, fontWeight: 700,
        cursor: 'pointer', fontFamily: sans, letterSpacing: 0.2,
        boxShadow: '0 10px 30px rgba(0,0,0,0.22)',
    };
    const ghostBtn: React.CSSProperties = {
        background: 'transparent', color: CREAM, border: `1.5px solid rgba(251,247,244,0.55)`,
        borderRadius: 999, padding: narrow ? '15px 26px' : '17px 34px', fontSize: narrow ? 16 : 17,
        fontWeight: 600, cursor: 'pointer', fontFamily: sans,
    };

    return (
        <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', background: RED, color: CREAM, fontFamily: sans, zIndex: 900 }}>
            {/* ---------- Hero ---------- */}
            <section style={{ minHeight: narrow ? 'auto' : '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: narrow ? '72px 22px 56px' : '0 8vw' }}>
                <div style={{ maxWidth: 1180, margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1.05fr 1fr', gap: narrow ? 40 : 64, alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: 13, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.72, marginBottom: 22, fontWeight: 600 }}>
                            3D Presenter
                        </div>
                        <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: narrow ? 40 : 66, lineHeight: 1.06, margin: '0 0 24px', letterSpacing: -0.5 }}>
                            Präsentationen,<br />durch die man<br /><em style={{ fontStyle: 'italic' }}>hindurchfliegt</em>.
                        </h1>
                        <p style={{ fontSize: narrow ? 17 : 20, lineHeight: 1.55, opacity: 0.9, margin: '0 0 38px', maxWidth: 520 }}>
                            Aus einer Vorlage und deinen eigenen Bildern, Videos und 3D-Modellen wird eine
                            räumliche Geschichte. Die Kamera führt durch den Raum statt Folien umzublättern —
                            im Browser, ohne Installation, teilbar als Link.
                        </p>
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                            <button style={primaryBtn} onClick={() => (onCreate ? onCreate() : (window.location.href = '/edit/new'))}>
                                Jetzt gestalten
                            </button>
                            <button style={ghostBtn} onClick={() => (window.location.href = demoUrl)}>
                                Demo ansehen
                            </button>
                        </div>
                        <div style={{ marginTop: 26, fontSize: 14, opacity: 0.72 }}>
                            Kein Konto nötig zum Ausprobieren · Anmeldung erst beim Speichern ·{' '}
                            <a href="/auth" data-testid="landing-account-link" style={{ color: CREAM, textDecoration: 'underline' }}>Konto anlegen</a>
                        </div>
                    </div>

                    {/* Live proof: the product presenting itself, running in an iframe. */}
                    <div style={{ borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(251,247,244,0.28)', boxShadow: '0 30px 80px rgba(0,0,0,0.35)', background: RED_DEEP, aspectRatio: narrow ? '16 / 11' : '4 / 3' }}>
                        <iframe
                            src={`/?pres=${heroPresentationId}&embed=1`}
                            title="Live-Demo"
                            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                            allow="fullscreen"
                        />
                    </div>
                </div>
            </section>

            {/* ---------- So sieht es aus: echte Bildschirmfotos ---------- */}
            <section data-testid="landing-gallery" style={{ background: RED_DEEP, padding: narrow ? '54px 22px' : '84px 8vw' }}>
                <div style={{ maxWidth: 1180, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: narrow ? 30 : 42, margin: '0 0 12px', lineHeight: 1.15 }}>
                        Das hier ist keine Folie.<br />Sondern ein Raum, durch den du dich bewegst.
                    </h2>
                    <p style={{ fontSize: narrow ? 15.5 : 17, opacity: 0.82, margin: '0 0 34px', maxWidth: 620, lineHeight: 1.55 }}>
                        Bildschirmfotos aus laufenden Präsentationen, keine Renderings. Jedes Bild öffnet die Präsentation im Browser.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: narrow ? 22 : 24 }}>
                        {GALLERY.map(g => (
                            <a key={g.pres} href={`/?pres=${g.pres}&view=1`} style={{ color: CREAM, textDecoration: 'none', display: 'block' }}>
                                <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(251,247,244,0.22)', boxShadow: '0 22px 60px rgba(0,0,0,0.32)', background: RED, aspectRatio: '16 / 10' }}>
                                    <img src={g.src} alt={g.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                </div>
                                <div style={{ padding: '14px 4px 0' }}>
                                    <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 5 }}>{g.title}</div>
                                    <div style={{ fontSize: 14.5, lineHeight: 1.55, opacity: 0.8 }}>{g.text}</div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- Was es kann ---------- */}
            <section style={{ background: CREAM, color: INK, padding: narrow ? '58px 22px' : '96px 8vw' }}>
                <div style={{ maxWidth: 1120, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: narrow ? 30 : 42, margin: '0 0 46px', lineHeight: 1.15, color: HEAD_ON_CREAM }}>
                        Warum das anders wirkt als eine Folie
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)', gap: narrow ? 34 : 44 }}>
                        {[
                            { k: 'Struktur', t: 'Der Raum trägt die Struktur', d: 'Verwandte Inhalte stehen beieinander, Zusammenhänge sind sichtbar statt behauptet. Die Kamera fährt hin — das Publikum behält die Orientierung.' },
                            { k: 'Inhalte', t: 'Echte Objekte, nicht nur Bilder', d: 'Fotos, Video und 3D-Modelle im selben Raum. Ein Produkt lässt sich umrunden, ein Gelände überfliegen, ein Detail heranholen.' },
                            { k: 'Verbreitung', t: 'Ein Link genügt', d: 'Fertige Geschichten laufen im Browser — auf dem Notebook im Termin, auf dem Handy beim Kunden, eingebettet auf der eigenen Website.' },
                        ].map(f => (
                            <div key={f.t}>
                                {/* Exakt die Hero-Hierarchie, nur invertiert: gesperrtes Mini-Label
                                    (Sans), Titel (Sans, kräftig), Fließtext (Sans). Serif bleibt der
                                    großen Sektionsüberschrift vorbehalten; Trennstriche gibt es im
                                    roten Bereich nicht, also hier auch nicht. */}
                                <div style={{ fontSize: 12, letterSpacing: 2.6, textTransform: 'uppercase', fontWeight: 700, color: RED, opacity: 0.75, marginBottom: 12 }}>{f.k}</div>
                                <h3 style={{ fontSize: 20, margin: '0 0 12px', fontWeight: 700, letterSpacing: -0.2, color: HEAD_ON_CREAM }}>{f.t}</h3>
                                <p style={{ fontSize: 16, lineHeight: 1.62, margin: 0, opacity: 0.82 }}>{f.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- So entsteht eine Story ---------- */}
            <section style={{ padding: narrow ? '58px 22px' : '92px 8vw' }}>
                <div style={{ maxWidth: 1120, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: narrow ? 30 : 42, margin: '0 0 44px' }}>In drei Schritten</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)', gap: narrow ? 30 : 40 }}>
                        {[
                            ['01', 'Mit einer Vorlage beginnen', 'Du startest mit drei Stationen im Raum. Jede Überschrift wird ein Ort, jeder Text ein Halt der Kamera.'],
                            ['02', 'Anordnen und füllen', 'Stationen verschieben, eigene Bilder, Videos und 3D-Modelle einsetzen. Die Vorschau zeigt sofort, wie es wirkt.'],
                            ['03', 'Teilen oder vortragen', 'Link verschicken, auf der Website einbetten oder im Vollbild präsentieren.'],
                        ].map(([n, t, d]) => (
                            <div key={n}>
                                <div style={{ fontSize: 12, letterSpacing: 2.6, fontWeight: 700, opacity: 0.6, marginBottom: 12 }}>SCHRITT {n}</div>
                                <h3 style={{ fontSize: 19, margin: '0 0 10px', fontWeight: 700 }}>{t}</h3>
                                <p style={{ fontSize: 15.5, lineHeight: 1.6, margin: 0, opacity: 0.85 }}>{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- Abschluss ---------- */}
            <section style={{ background: RED_DEEP, padding: narrow ? '60px 22px' : '96px 8vw', textAlign: 'center' }}>
                <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: narrow ? 30 : 46, margin: '0 0 18px' }}>
                    Bau deine erste Geschichte
                </h2>
                <p style={{ fontSize: 17, opacity: 0.85, margin: '0 auto 34px', maxWidth: 540, lineHeight: 1.55 }}>
                    Ohne Konto ausprobieren. Anmelden musst du dich erst, wenn du speichern oder teilen willst.
                </p>
                <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button style={primaryBtn} onClick={() => (onCreate ? onCreate() : (window.location.href = '/edit/new'))}>
                        Jetzt starten
                    </button>
                    <button style={ghostBtn} onClick={() => (onOpenApp ? onOpenApp() : (window.location.href = '/?app=1'))}>
                        Meine Präsentationen
                    </button>
                </div>
            </section>

            <footer style={{ padding: '30px 22px 44px', textAlign: 'center', fontSize: 13.5, opacity: 0.62 }}>
                3D Presenter · Arkturian · <a href="/pricing" style={{ color: CREAM, opacity: 0.85 }}>Preise</a> · <a href="/auth" style={{ color: CREAM, opacity: 0.85 }}>Konto</a>
            </footer>
        </div>
    );
};

export default MarketingHome;
